import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

/**
 * Drop-in, crash-proof replacement for Angular's built-in `currency` pipe.
 *
 * The built-in `CurrencyPipe` throws `InvalidPipeArgument` (Angular error
 * NG02100) whenever it receives a value it cannot convert to a number — e.g. a
 * non-numeric string, an object, or `NaN`. A single bad value crashes the whole
 * view and floods production error logs (the same class of failure we hit with
 * dates — see SafeDatePipe).
 *
 * `safeCurrency` delegates formatting to `CurrencyPipe` (so output is identical
 * for valid inputs) but returns '' for null/empty/invalid values instead of
 * throwing. Standardise currency rendering on this pipe so NG02100 from money
 * values cannot recur.
 *
 * Usage is identical to `currency`:  {{ value | safeCurrency:'USD' }}
 */
@Pipe({
  name: 'safeCurrency',
  standalone: true,
})
export class SafeCurrencyPipe implements PipeTransform {
  private readonly currencyPipe: CurrencyPipe;

  constructor(@Inject(LOCALE_ID) locale: string) {
    this.currencyPipe = new CurrencyPipe(locale);
  }

  transform(
    value: any,
    currencyCode?: string,
    display: 'code' | 'symbol' | 'symbol-narrow' | string | boolean = 'symbol',
    digitsInfo?: string,
    locale?: string,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    try {
      return this.currencyPipe.transform(value, currencyCode, display, digitsInfo, locale) ?? '';
    } catch {
      // Invalid/non-numeric value — render blank rather than throwing NG02100.
      return '';
    }
  }
}
